import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import {
  clearIndexedDbPersistence,
  initializeFirestore,
  terminate,
  type Firestore
} from 'firebase/firestore';
import { getMissingFirebaseEnvKeys } from './firebase-config.js';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID
};

function requireConfig(): void {
  const missing = getMissingFirebaseEnvKeys();
  if (missing.length) {
    throw new Error(
      `Missing Firebase env: ${missing.join(', ')}. See apps/web/.env.local`
    );
  }
}

let app: FirebaseApp | undefined;
let auth: Auth | undefined;
let db: Firestore | undefined;

function firestoreSettings() {
  return { experimentalAutoDetectLongPolling: true } as const;
}

export function getFirebaseApp(): FirebaseApp {
  if (!app) {
    requireConfig();
    app = initializeApp(config);
  }
  return app;
}

export function getFirebaseAuth(): Auth {
  if (!auth) auth = getAuth(getFirebaseApp());
  return auth;
}

export function getFirestoreDb(): Firestore {
  if (!db) {
    db = initializeFirestore(getFirebaseApp(), firestoreSettings());
  }
  return db;
}

/** Drop Firestore client + IndexedDB cache so another account cannot read stale org/branding docs. */
export async function resetFirestoreSession(): Promise<void> {
  if (!db) return;
  const instance = db;
  db = undefined;
  try {
    await terminate(instance);
  } catch {
    /* best-effort */
  }
  try {
    await clearIndexedDbPersistence(instance);
  } catch {
    /* best-effort */
  }
}
